import { format, subMonths } from 'date-fns';

import { CareRefusal, ReadinessCheck } from './types';

// The only registered activity source in hero-rtm-api.
export const CARE_SOURCE = 'gateway';

// Program and role labels from the registered Care candidate catalog; unknown codes remain visible.
export const CARE_CODE_CATALOG: ReadonlyMap<string, { program: string; role: string }> = new Map([
    ['99490', { program: 'CCM', role: 'Base code' }],
    ['99439', { program: 'CCM', role: 'Additional units' }],
    ['98980', { program: 'RTM', role: 'Base code' }],
    ['98981', { program: 'RTM', role: 'Additional units' }],
    ['99484', { program: 'BHI', role: 'Base code' }]
]);
export const CARE_CODE_LABELS: ReadonlyMap<string, string> = new Map(
    Array.from(CARE_CODE_CATALOG, ([code, { program, role }]) => [code, `${program} · ${role}`])
);

const REASON_LABELS: Record<string, string> = {
    episode_data_invalid_requires_review: 'Review enrollment history',
    member_dates_invalid_requires_review: 'Review enrollment dates',
    open_episode_provider_mismatch_requires_review: 'Confirm enrollment organization',
    member_current_provider_unknown_requires_review: 'Confirm current care organization',
    same_program_reenrollment_requires_review: 'Review enrollment change',
    missing_from_minutes_csv: 'Missing minutes',
    csv_program_not_enrolled: 'Review missing program enrollment',
    termination_time_unverified: 'Review care on the end date',
    rpm_concurrency_review_required: 'Review RTM and RPM billing',
    condition_review_required: 'Review qualifying conditions',
    condition_not_met: 'Conditions not confirmed',
    rtm_cross_org_month_conflict: 'RTM already locked elsewhere',
    decision_state_invalid: 'Review saved decision',
    no_longer_eligible: 'No longer eligible',
    below_threshold: 'Below required minutes',
    below_addon_threshold: 'More time needed for additional units',
    base_not_billable: 'First code is not billable',
    base_needs_review: 'First code needs review',
    operator_block: 'Blocked by operator',
    operator_hold: 'Blocked by operator',
    outside_eligibility: 'Outside enrollment period',
    unreleased_flag: 'Review required',
    no_source_rows: 'Hero billing without imported activity',
    block: 'Blocked by operator',
    hold: 'Blocked by operator'
};

export const careReasonText = (reason: string | null): string | null => {
    if (!reason) return null;
    if (Object.prototype.hasOwnProperty.call(REASON_LABELS, reason)) return REASON_LABELS[reason];
    // Keep future reason codes readable until they have a specific label.
    const text = reason.replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
};

// Service months are UTC calendar months, whatever the reviewer's timezone.
const utcMonthStart = (now: Date): Date => new Date(now.getUTCFullYear(), now.getUTCMonth(), 1);

// Current month and the 11 before it, newest first.
export const monthOptions = (now: Date): string[] =>
    Array.from({ length: 12 }, (_, index) => format(subMonths(utcMonthStart(now), index), 'yyyy-MM'));

// Billing reviews a finished month, so that is the default.
export const previousMonth = (now: Date): string => format(subMonths(utcMonthStart(now), 1), 'yyyy-MM');

const ERROR_TEXT: Record<string, string> = {
    month_locked: 'This month is locked. Nothing can change it.',
    not_ready: 'The month is not ready to lock.',
    no_change: 'This action would change nothing.',
    invalid_reason: 'Enter a reason.',
    note_required: 'Enter a note.',
    approval_reference_required: 'Enter the approval reference.',
    invalid_provider: 'This organization has no care billing provider.',
    missing_active_import: 'An import is missing for this month.',
    import_period_not_month: 'An import covers a different period than this month.',
    unsupported_report_type: 'This is a units report. Upload the monthly minutes CSV.',
    stale_decision:
        'This code changed while the form was open. Review the refreshed evidence and reopen the action.',
    decision_not_allowed: 'This decision is no longer available. Review the refreshed code.',
    decision_state_invalid: 'This code has invalid decision history. Ask Ops to investigate.',
    ambiguous_match:
        'Some CSV rows match more than one patient. The previous active import is retained; correct the identities or rematch.',
    rematch_required:
        'Patient identities changed. Import the complete CSV again to match them.',
    csv_identity_unverified:
        'Reported minutes could not be linked to a current enrollment in this organization. Sync patients, check their enrollment and import again.',
    population_required: 'Sync patients for this month before importing minutes.',
    upload_too_large: 'The CSV exceeds the upload limit. Use a smaller complete monthly CSV.',
    malformed_file: 'The CSV format is invalid. Use the example header and MM:SS durations.',
    invalid_request: 'The request is invalid. Refresh the page and try again.'
};

export const CODE_ACTION_LABELS = {
    resolve_warning: 'Resolve decision',
    exclude: 'Make not billable',
    clear_exclusion: 'Clear exclusion',
    hold: 'Block code',
    clear_hold: 'Unblock code',
    note: 'Edit note'
};

export const noteActionLabel = (note: string | null | undefined): string =>
    note?.trim() ? 'Edit note' : 'Add note';

export const asRefusal = (error: unknown): CareRefusal | undefined =>
    typeof error === 'object' &&
    error !== null &&
    (error as CareRefusal).status === 'error' &&
    typeof (error as CareRefusal).message === 'string'
        ? (error as CareRefusal)
        : undefined;

export const careErrorText = (error: unknown): string => {
    const code = asRefusal(error)?.message;
    if (!code) {
        return 'The request failed. Try again.';
    }
    return ERROR_TEXT[code] ?? `The request was refused (${code}).`;
};

const numberDetail = (check: ReadinessCheck, key: string): number | null => {
    const value = check.detail[key];
    return typeof value === 'number' ? value : null;
};

const textDetail = (check: ReadinessCheck, key: string): string | null => {
    const value = check.detail[key];
    return typeof value === 'string' ? value : null;
};

const rowsText = (check: ReadinessCheck, state: string): string => {
    const value = numberDetail(check, 'count');
    if (value === null) {
        return `Some rows are ${state}.`;
    }
    return `${value} ${value === 1 ? 'row is' : 'rows are'} ${state}.`;
};

export const codeText = (code: string): string => ERROR_TEXT[code] ?? `(${code})`;

const NO_RUN = 'No successful billing calculation yet. Select Calculate billable codes.';

const DRY_RUN_LABELS: Record<string, string> = {
    population_dry_run_unchanged: 'Patient sync check',
    qualification_dry_run_unchanged: 'Billing calculation check'
};

// Check names are hero-rtm-api readiness.py; an unknown name falls back to the raw name.
const CHECK_TEXT: Record<string, (check: ReadinessCheck) => string> = {
    month_unlocked: () => 'The month is already locked.',
    latest_run_succeeded: (check) =>
        check.detail.run_id === null
            ? NO_RUN
            : 'The latest billing calculation did not succeed. Select Calculate billable codes to try again.',
    latest_run_not_draft: (check) => {
        const rule = textDetail(check, 'rule_version');
        return rule
            ? `The rule ${rule} is a draft. A month can lock only after Product approves the rule.`
            : NO_RUN;
    },
    active_imports: (check) => {
        const code = textDetail(check, 'code');
        return code
            ? `The imports cannot be used: ${codeText(code)}`
            : 'An import is missing for this month.';
    },
    inputs_unchanged: (check) =>
        check.detail.run_id === null
            ? NO_RUN
            : 'Something changed after the last billing calculation. Select Calculate billable codes to update the results.',
    no_ungraded: (check) =>
        `${rowsText(check, 'not calculated yet')} Select Calculate billable codes.`,
    no_incomplete: (check) =>
        `${rowsText(check, 'Needs review')} Resolve the code decisions or correct the source data, then select Calculate billable codes.`,
    no_hold: (check) =>
        `${rowsText(check, 'Blocked')} Unblock the affected codes, then select Calculate billable codes.`,
    reconciliation_explained: (check) => {
        const lines = numberDetail(check, 'unexplained_lines');
        if (lines === null) {
            return 'Reconciliation needs both imports to be active.';
        }
        return `${lines} patient-code ${lines === 1 ? 'line differs' : 'lines differ'} from the source with no named cause. See Reconciliation.`;
    },
    population_dry_run_unchanged: () =>
        'Patient records need updating. Select Sync patients, then Calculate billable codes.',
    qualification_dry_run_unchanged: () =>
        'Billing results need updating. Select Calculate billable codes.'
};

export const checkText = (check: ReadinessCheck): string => {
    const label = DRY_RUN_LABELS[check.name] ?? check.name;
    if (textDetail(check, 'skipped')) {
        return `${label}: not checked until the checks above pass.`;
    }
    const code = textDetail(check, 'code');
    if (code && check.name in DRY_RUN_LABELS) {
        return `${label} could not run (${code}). Try again later.`;
    }
    return CHECK_TEXT[check.name]?.(check) ?? check.name;
};
